import type { ContentPosition } from '@sunwebgroup/ui-components/uds'
import { useIntl } from '@/intl'
import { Item } from '@/scopes/search/components/QuickSearchBar/Item'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { Popover } from '@sunwebgroup/ui-components/uds'
import { Sheet } from '@sunwebgroup/ui-components/uds/index'
import * as React from 'react'
import { styled } from 'styled-components'

interface FilterProps {
  id: string
  placeholder: string
  title: string
  onConfirm: () => void
  className?: string
  position?: ContentPosition
  additionalSelectedItems?: number
  selectionFormattedText?: string
  compactSelectionFormattedText?: string
  asSheet?: boolean
  hasPadding?: boolean
}

function FilterItem({
  id,
  placeholder,
  title,
  children,
  onConfirm,
  additionalSelectedItems,
  selectionFormattedText,
  compactSelectionFormattedText,
  asSheet,
  hasPadding = true,
  ...rest
}: React.PropsWithChildren<FilterProps>) {
  const intl = useIntl()
  const { activeItemId, setActiveItem } = useQuickSearchStore()
  const isActive = activeItemId === id
  const hasActiveSibling = activeItemId !== null && !isActive

  const toggleActiveState = () => {
    if (isActive) {
      setActiveItem(null)
      onConfirm()
      return
    }
    setActiveItem(id)
  }

  if (asSheet) {
    return (
      <Sheet.Root open={isActive} onOpenChange={toggleActiveState}>
        <Sheet.Trigger>
          <Item
            placeholder={placeholder}
            title={title}
            isActive={isActive}
            hasActiveSibling={hasActiveSibling}
            additionalSelectedItems={additionalSelectedItems}
            selectionFormattedText={selectionFormattedText}
          />
        </Sheet.Trigger>
        <Sheet.Body side="right" hasPadding={hasPadding}>
          <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={title} />
          <Sheet.Content>{children}</Sheet.Content>
        </Sheet.Body>
      </Sheet.Root>
    )
  }

  return (
    <Popover.Root
      open={isActive}
      onOpenChange={toggleActiveState}
    >
      <Popover.Trigger>
        <Item
          placeholder={placeholder}
          title={title}
          isActive={isActive}
          hasActiveSibling={hasActiveSibling}
          additionalSelectedItems={additionalSelectedItems}
          selectionFormattedText={selectionFormattedText}
        />
      </Popover.Trigger>
      <Popover.Content
        hasPadding={hasPadding}
        title={title}
        closeText={intl.formatMessage({ id: 'close' })}
        {...rest}
      >
        {children}
      </Popover.Content>
    </Popover.Root>
  )
}

export const Filter = styled(FilterItem)``
